import { useState, useEffect, useRef } from 'react';
import { P, cardStyle, inputStyle, labelStyle } from '../styles/theme';
import Btn from './Shared/Btn';
import {
  fetchCategories, insertCategory, updateCategory, reorderCategories,
  fetchServices, insertService, updateService, deleteService, reorderServices,
} from '../services/serviceBuilderService';

export default function ServiceBuilder() {
  const [categories,    setCategories]    = useState([]);
  const [services,      setServices]      = useState([]);
  const [expandedCatId, setExpandedCatId] = useState(null);

  // New category form
  const [newCatName, setNewCatName] = useState('');
  const [newCatType, setNewCatType] = useState('single');

  // Edit category inline
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCat,   setEditingCat]   = useState({ name: '', selection_type: 'single' });

  // New service form (shared across categories, reset on expand change)
  const [newSvc, setNewSvc] = useState({ name: '', price: '', duration: '', description: '' });

  // Edit service inline
  const [editingSvcId, setEditingSvcId] = useState(null);
  const [editingSvc,   setEditingSvc]   = useState({ name: '', price: '', duration: '', description: '' });

  // Drag — categories
  const [draggingCatId, setDraggingCatId] = useState(null);
  const [dragOverCatId, setDragOverCatId] = useState(null);
  const catTouchRef = useRef({ fromId: null, toId: null });

  // Drag — services
  const [draggingSvcId, setDraggingSvcId] = useState(null);
  const [dragOverSvcId, setDragOverSvcId] = useState(null);
  const svcTouchRef = useRef({ fromId: null, toId: null });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [cats, svcs] = await Promise.all([fetchCategories(), fetchServices()]);
      setCategories(cats);
      setServices(svcs);
    } catch (err) { alert('Load error: ' + err.message); }
  };

  const getCatServices = (catId) =>
    services
      .filter(s => s.category_id === catId)
      .sort((a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity));

  // ── Category actions ────────────────────────────────────────────
  const addCategory = async () => {
    if (!newCatName.trim()) return;
    const nextOrder = categories.length + 1;
    try {
      await insertCategory({ name: newCatName.trim(), selection_type: newCatType, sort_order: nextOrder });
      setNewCatName(''); setNewCatType('single');
      loadAll();
    } catch (err) { alert('Error: ' + err.message); }
  };

  const saveEditCat = async () => {
    if (!editingCat.name.trim()) return;
    await updateCategory(editingCatId, { name: editingCat.name.trim(), selection_type: editingCat.selection_type });
    setEditingCatId(null); loadAll();
  };

  const toggleCatActive = async (cat) => {
    await updateCategory(cat.id, { is_active: !cat.is_active }); loadAll();
  };

  const handleCatReorder = async (fromId, toId) => {
    const fromIdx = categories.findIndex(c => c.id === fromId);
    const toIdx   = categories.findIndex(c => c.id === toId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
    const reordered = [...categories];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setCategories(reordered);
    await reorderCategories(reordered);
    loadAll();
  };

  // ── Service actions ─────────────────────────────────────────────
  const addService = async (catId) => {
    if (!newSvc.name.trim()) return alert('Service name is required.');
    const nextOrder = getCatServices(catId).length + 1;
    try {
      await insertService({
        name:        newSvc.name.trim(),
        price:       Number(newSvc.price) || 0,
        duration:    Number(newSvc.duration) || 0,
        description: newSvc.description.trim() || null,
        category_id: catId,
        sort_order:  nextOrder,
        is_active:   true,
      });
      setNewSvc({ name: '', price: '', duration: '', description: '' });
      loadAll();
    } catch (err) { alert('Error: ' + err.message); }
  };

  const saveEditSvc = async () => {
    if (!editingSvc.name.trim()) return;
    await updateService(editingSvcId, {
      name:        editingSvc.name.trim(),
      price:       Number(editingSvc.price) || 0,
      duration:    Number(editingSvc.duration) || 0,
      description: editingSvc.description.trim() || null,
    });
    setEditingSvcId(null); loadAll();
  };

  const toggleSvcActive = async (svc) => {
    await updateService(svc.id, { is_active: !svc.is_active }); loadAll();
  };

  const handleDeleteSvc = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await deleteService(id); loadAll();
  };

  const handleSvcReorder = async (catId, fromId, toId) => {
    const catSvcs = getCatServices(catId);
    const fromIdx = catSvcs.findIndex(s => s.id === fromId);
    const toIdx   = catSvcs.findIndex(s => s.id === toId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
    const reordered = [...catSvcs];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    await reorderServices(reordered);
    loadAll();
  };

  const iconBtn = (onClick, icon, color) => (
    <button
      onClick={onClick}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: color || P.muted, fontSize: '16px', lineHeight: 1, padding: '4px 6px' }}
    >
      {icon}
    </button>
  );

  const typeTag = (type) => (
    <span style={{
      fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '12px', flexShrink: 0,
      background: type === 'single' ? P.rose + '20' : P.green + '20',
      color: type === 'single' ? P.rose : P.green,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>
      {type === 'single' ? 'Single' : 'Multi'}
    </span>
  );

  return (
    <div>
      <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: P.deep, fontWeight: 600, marginBottom: '4px' }}>
        Service Configuration
      </h2>
      <p style={{ fontSize: '13px', color: P.muted, marginBottom: '20px' }}>
        Changes appear immediately on the booking page — no code required.
      </p>

      {/* ── Add Category ─────────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '15px', color: P.deep, marginBottom: '12px', fontWeight: 700 }}>+ Add Category</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>Category Name</label>
            <input
              style={inputStyle}
              placeholder="e.g. Nail Art"
              value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCategory()}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Selection Type</label>
            <select style={inputStyle} value={newCatType} onChange={e => setNewCatType(e.target.value)}>
              <option value="single">Single Select (radio)</option>
              <option value="multi">Multi Select (checkbox)</option>
            </select>
          </div>
          <Btn onClick={addCategory} size="sm" style={{ flexShrink: 0 }}>Add</Btn>
        </div>
      </div>

      {/* ── Category List ─────────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '15px', color: P.deep, marginBottom: '14px', fontWeight: 700 }}>
          Categories{' '}
          <span style={{ color: P.muted, fontWeight: 400, fontSize: '12px' }}>— drag to reorder</span>
        </h3>

        {categories.length === 0 && (
          <p style={{ fontSize: '13px', color: P.muted }}>No categories yet. Add one above.</p>
        )}

        {categories.map(cat => {
          const catSvcs    = getCatServices(cat.id);
          const isExpanded = expandedCatId === cat.id;
          const isEditCat  = editingCatId === cat.id;

          return (
            <div
              key={cat.id}
              data-cat-id={cat.id}
              style={{
                marginBottom: '10px', borderRadius: '12px',
                border: `1.5px solid ${isExpanded ? P.rose : P.border}`,
                overflow: 'hidden',
                opacity: draggingCatId === cat.id ? 0.45 : 1,
                transition: 'opacity 0.12s',
              }}
            >
              {/* Category header row */}
              <div
                draggable
                onDragStart={e => { e.dataTransfer.setData('text/plain', cat.id); e.dataTransfer.effectAllowed = 'move'; setDraggingCatId(cat.id); }}
                onDragOver={e => { e.preventDefault(); setDragOverCatId(cat.id); }}
                onDragEnd={() => { setDraggingCatId(null); setDragOverCatId(null); }}
                onDrop={e => { e.preventDefault(); const from = e.dataTransfer.getData('text/plain'); if (from && from !== cat.id) handleCatReorder(from, cat.id); setDraggingCatId(null); setDragOverCatId(null); }}
                onTouchStart={() => { catTouchRef.current.fromId = cat.id; catTouchRef.current.toId = null; setDraggingCatId(cat.id); }}
                onTouchMove={e => { e.preventDefault(); const touch = e.touches[0]; const el = document.elementFromPoint(touch.clientX, touch.clientY); const target = el?.closest('[data-cat-id]'); const overId = target?.getAttribute('data-cat-id') || null; catTouchRef.current.toId = overId; setDragOverCatId(overId); }}
                onTouchEnd={() => { const { fromId, toId } = catTouchRef.current; if (fromId && toId && fromId !== toId) handleCatReorder(fromId, toId); catTouchRef.current = { fromId: null, toId: null }; setDraggingCatId(null); setDragOverCatId(null); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 14px',
                  background: dragOverCatId === cat.id ? P.peach + '60' : isExpanded ? P.peach + '25' : 'transparent',
                  cursor: 'pointer', transition: 'background 0.12s',
                  touchAction: 'none',
                }}
                onClick={() => { if (!isEditCat) { setExpandedCatId(prev => prev === cat.id ? null : cat.id); setNewSvc({ name: '', price: '', duration: '', description: '' }); setEditingSvcId(null); } }}
              >
                <span style={{ color: P.muted, fontSize: '18px', cursor: 'grab', userSelect: 'none', flexShrink: 0 }}>☰</span>

                {isEditCat ? (
                  <>
                    <input
                      style={{ ...inputStyle, flex: 2, padding: '6px 10px' }}
                      value={editingCat.name}
                      onChange={e => setEditingCat({ ...editingCat, name: e.target.value })}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => { if (e.key === 'Enter') saveEditCat(); if (e.key === 'Escape') setEditingCatId(null); }}
                      autoFocus
                    />
                    <select
                      style={{ ...inputStyle, flex: 1, padding: '6px 10px' }}
                      value={editingCat.selection_type}
                      onChange={e => setEditingCat({ ...editingCat, selection_type: e.target.value })}
                      onClick={e => e.stopPropagation()}
                    >
                      <option value="single">Single Select</option>
                      <option value="multi">Multi Select</option>
                    </select>
                    <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                      <Btn size="sm" onClick={saveEditCat}>Save</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => setEditingCatId(null)}>✕</Btn>
                    </div>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontWeight: 700, fontSize: '14px', color: cat.is_active ? P.deep : P.muted }}>
                      {cat.name}
                    </span>
                    {typeTag(cat.selection_type)}
                    <span style={{ fontSize: '11px', color: P.muted, flexShrink: 0 }}>{catSvcs.length} services</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => toggleCatActive(cat)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', padding: '3px 7px', borderRadius: '6px', color: cat.is_active ? P.green : P.muted, fontWeight: 700 }}
                      >
                        {cat.is_active ? '● Active' : '○ Off'}
                      </button>
                      {iconBtn(() => { setEditingCatId(cat.id); setEditingCat({ name: cat.name, selection_type: cat.selection_type }); }, '✏️')}
                    </div>
                    <span style={{ fontSize: '12px', color: P.muted, flexShrink: 0 }}>{isExpanded ? '▲' : '▼'}</span>
                  </>
                )}
              </div>

              {/* ── Expanded: service list + add form ─────────── */}
              {isExpanded && (
                <div style={{ borderTop: `1px solid ${P.border}`, padding: '14px 16px' }}>

                  {/* Service list */}
                  {catSvcs.length === 0 && (
                    <p style={{ fontSize: '13px', color: P.muted, marginBottom: '16px' }}>
                      No services yet — add one below.
                    </p>
                  )}

                  {catSvcs.map(svc => (
                    editingSvcId === svc.id ? (
                      <div key={svc.id} style={{ padding: '12px 0', borderBottom: `1px solid ${P.border}` }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                          <div>
                            <label style={labelStyle}>Name</label>
                            <input style={{ ...inputStyle, padding: '7px 10px' }} value={editingSvc.name} onChange={e => setEditingSvc({ ...editingSvc, name: e.target.value })} />
                          </div>
                          <div>
                            <label style={labelStyle}>Price ($)</label>
                            <input type="number" min="0" style={{ ...inputStyle, padding: '7px 10px' }} value={editingSvc.price} onChange={e => setEditingSvc({ ...editingSvc, price: e.target.value })} />
                          </div>
                          <div>
                            <label style={labelStyle}>Duration (min)</label>
                            <input type="number" min="0" style={{ ...inputStyle, padding: '7px 10px' }} value={editingSvc.duration} onChange={e => setEditingSvc({ ...editingSvc, duration: e.target.value })} />
                          </div>
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          <label style={labelStyle}>Description (shown to customer)</label>
                          <textarea
                            style={{ ...inputStyle, height: '60px', resize: 'none' }}
                            placeholder="e.g. Simple designs, minimal detail"
                            value={editingSvc.description}
                            onChange={e => setEditingSvc({ ...editingSvc, description: e.target.value })}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <Btn size="sm" onClick={saveEditSvc}>Save</Btn>
                          <Btn size="sm" variant="ghost" onClick={() => setEditingSvcId(null)}>Cancel</Btn>
                        </div>
                      </div>
                    ) : (
                      <div
                        key={svc.id}
                        data-svc-id={svc.id}
                        draggable
                        onDragStart={e => { e.dataTransfer.setData('text/plain', svc.id); e.dataTransfer.effectAllowed = 'move'; setDraggingSvcId(svc.id); }}
                        onDragOver={e => { e.preventDefault(); setDragOverSvcId(svc.id); }}
                        onDragEnd={() => { setDraggingSvcId(null); setDragOverSvcId(null); }}
                        onDrop={e => { e.preventDefault(); const from = e.dataTransfer.getData('text/plain'); if (from && from !== svc.id) handleSvcReorder(cat.id, from, svc.id); setDraggingSvcId(null); setDragOverSvcId(null); }}
                        onTouchStart={() => { svcTouchRef.current.fromId = svc.id; svcTouchRef.current.toId = null; setDraggingSvcId(svc.id); }}
                        onTouchMove={e => { e.preventDefault(); const touch = e.touches[0]; const el = document.elementFromPoint(touch.clientX, touch.clientY); const target = el?.closest('[data-svc-id]'); const overId = target?.getAttribute('data-svc-id') || null; svcTouchRef.current.toId = overId; setDragOverSvcId(overId); }}
                        onTouchEnd={() => { const { fromId, toId } = svcTouchRef.current; if (fromId && toId && fromId !== toId) handleSvcReorder(cat.id, fromId, toId); svcTouchRef.current = { fromId: null, toId: null }; setDraggingSvcId(null); setDragOverSvcId(null); }}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '11px 0',
                          borderBottom: `1px solid ${P.border}`,
                          background: dragOverSvcId === svc.id ? P.peach + '60' : 'transparent',
                          opacity: draggingSvcId === svc.id ? 0.4 : 1,
                          transition: 'background 0.12s, opacity 0.12s',
                          touchAction: 'none',
                        }}
                      >
                        <span style={{ color: P.muted, fontSize: '16px', cursor: 'grab', userSelect: 'none', paddingTop: '2px', flexShrink: 0 }}>☰</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: '14px', color: svc.is_active ? P.deep : P.muted }}>{svc.name}</strong>
                            <span style={{ color: P.rose, fontWeight: 700, fontSize: '13px' }}>${svc.price}</span>
                            {Number(svc.duration) > 0 && (
                              <span style={{ fontSize: '12px', color: P.muted }}>⏰ {svc.duration}min</span>
                            )}
                          </div>
                          {svc.description && (
                            <p style={{ fontSize: '12px', color: P.muted, marginTop: '3px', marginBottom: 0, lineHeight: '1.4' }}>
                              {svc.description}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                          <button
                            onClick={() => toggleSvcActive(svc)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', padding: '3px 6px', borderRadius: '6px', color: svc.is_active ? P.green : P.muted, fontWeight: 700, whiteSpace: 'nowrap' }}
                          >
                            {svc.is_active ? '● On' : '○ Off'}
                          </button>
                          {iconBtn(() => { setEditingSvcId(svc.id); setEditingSvc({ name: svc.name, price: String(svc.price || 0), duration: String(svc.duration || 0), description: svc.description || '' }); }, '✏️')}
                          {iconBtn(() => handleDeleteSvc(svc.id), '×', P.red)}
                        </div>
                      </div>
                    )
                  ))}

                  {/* Add service form */}
                  <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: `1px dashed ${P.border}` }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                      Add Service to {cat.name}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                      <div>
                        <label style={labelStyle}>Name *</label>
                        <input style={inputStyle} placeholder="e.g. Tier 1" value={newSvc.name} onChange={e => setNewSvc({ ...newSvc, name: e.target.value })} />
                      </div>
                      <div>
                        <label style={labelStyle}>Price ($)</label>
                        <input type="number" min="0" style={inputStyle} placeholder="0" value={newSvc.price} onChange={e => setNewSvc({ ...newSvc, price: e.target.value })} />
                      </div>
                      <div>
                        <label style={labelStyle}>Duration (min)</label>
                        <input type="number" min="0" style={inputStyle} placeholder="0" value={newSvc.duration} onChange={e => setNewSvc({ ...newSvc, duration: e.target.value })} />
                      </div>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={labelStyle}>Description (optional — shown to customer)</label>
                      <textarea
                        style={{ ...inputStyle, height: '56px', resize: 'none' }}
                        placeholder="e.g. Simple designs, minimal detail"
                        value={newSvc.description}
                        onChange={e => setNewSvc({ ...newSvc, description: e.target.value })}
                      />
                    </div>
                    <Btn onClick={() => addService(cat.id)} style={{ width: '100%' }}>
                      + Add Service
                    </Btn>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
